/**
 * @fileoverview Static-receptor nonbonded contribution.
 *
 * During a minimization the receptor is an external constant: its atoms never
 * move, so only the *ligand-side* gradient is computed here. The receptor's own
 * gradient would be discarded, so it is never formed.
 *
 * Two-phase design:
 *   1. `prepareReceptor()` runs once per receptor (reused across all
 *      minimizations of all ligands in the same pocket). It prunes the full
 *      receptor down to the atoms that can possibly interact with a ligand in
 *      the pocket -- mirroring bfd-server's 15 A / 1.5 nm residue-cutoff prune
 *      -- and packs the survivors' coords/charge/sigma/eps into compact
 *      `Float64Array`s on the instance. This keeps the hot inner loop tight
 *      and cache-friendly.
 *   2. `evaluate()` runs every energy/gradient call. For each ligand atom it
 *      sweeps the stored candidate receptor atoms, applies a plain (truncated)
 *      distance cutoff, and accumulates LJ + Coulomb via the shared `pairwise`
 *      primitives, writing the ligand-side gradient only.
 *
 * Combining rules: Lorentz-Berthelot. Sigma is the arithmetic mean
 * (combineSigma); epsilon is the geometric mean, combined in the hot loop as
 * sqrtEps_i * sqrtEps_j from precomputed per-atom sqrt(epsilon) -- numerically
 * equal to sqrt(eps_i*eps_j) to round-off, but with the invariant sqrt hoisted
 * out of the O(N_lig * N_rec) per-pair loop. No 1-4 scaling: ligand-receptor
 * pairs are intermolecular and therefore never bonded. Plain truncated cutoff
 * (no potential shift).
 *
 * Internal units: nm, kJ/mol, e (the engine contract).
 *
 * See: proto/src/chemistry/LocalMinimizer/types.js (ReceptorAtoms),
 *      proto/src/chemistry/LocalMinimizer/forces/pairwise.js.
 */

import { combineSigma, ljPair, coulombPair } from './pairwise.js';

/**
 * @typedef {import('../types.js').ReceptorAtoms} ReceptorAtoms
 */

/**
 * Pre-filtered, static receptor wrapped for repeated ligand evaluations.
 *
 * Instances are stateful: call {@link ReceptorContext#prepareReceptor} once to
 * load + prune a receptor, then call {@link ReceptorContext#evaluate} as many
 * times as needed during minimization. The candidate buffers are owned by the
 * instance and never reallocated by `evaluate` (no hot-path allocation).
 */
export class ReceptorContext {
    constructor() {
        /**
         * Number of candidate (in-pocket) receptor atoms retained by the most
         * recent `prepareReceptor` call.
         * @type {number}
         */
        this.nCandidates = 0;
        /** Length 3*nCandidates, nm. @type {Float64Array} */
        this.coords = new Float64Array(0);
        /** Length nCandidates, e. @type {Float64Array} */
        this.charges = new Float64Array(0);
        /** Length nCandidates, nm. @type {Float64Array} */
        this.sigma = new Float64Array(0);
        /** Length nCandidates, kJ/mol. @type {Float64Array} */
        this.epsilon = new Float64Array(0);
        /**
         * Precomputed per-atom sqrt(epsilon), length nCandidates. Lets the hot
         * loop combine LJ epsilon as sqrtEps_i * sqrtEps_j (a multiply) instead
         * of sqrt(eps_i*eps_j) (a per-pair sqrt); receptor epsilons are constant
         * across a minimization, so the sqrt is hoisted here. Built by
         * prepareReceptor; lazily (re)built by evaluate() when it doesn't match
         * `epsilon` -- the worker's rebuildReceptor assigns the buffers directly
         * (on a fresh instance) without going through prepareReceptor.
         * @type {Float64Array}
         */
        this.sqrtEpsilon = new Float64Array(0);
        /**
         * Cached per-call ligand sqrt(epsilon) and the epsilon array it was
         * built from. The ligand epsilon array is constant during a minimization
         * (only coords change), so this is rebuilt only when the passed array
         * identity changes -- keeping a sqrt sweep out of every evaluation.
         * @type {Float64Array|null}
         */
        this._ligSqrtEps = null;
        /** @type {Float64Array|null} */
        this._ligEpsRef = null;
        /**
         * Cutoff (nm) captured at prepareReceptor time and used to size the
         * prune radius. `evaluate` also takes a cutoff argument; the two are
         * expected to agree (the same cutoff governs both pruning and the
         * per-pair test).
         * @type {number}
         */
        this.cutoff = 0;
    }

    /**
     * Pre-filter the receptor to atoms that can interact with any ligand atom
     * inside the pocket, and pack the survivors into the instance's compact
     * buffers.
     *
     * The retention test is squared-distance based (no sqrt): a receptor atom
     * is kept iff its distance to `pocketCenter` is <= the prune radius
     *
     *     R = pocketRadius + cutoff + cutoffBuffer
     *
     * i.e. it lies within `cutoff (+ buffer)` of the pocket sphere surface,
     * so a ligand atom anywhere inside the pocket sphere could still be within
     * `cutoff` of it. `cutoffBuffer` is a safety margin (0 in practice, because
     * the engine applies a true per-pair cutoff in `evaluate`).
     *
     * @param {ReceptorAtoms} receptor  Full receptor atoms (coords nm, charges e,
     *   sigma nm, epsilon kJ/mol). Read-only; not retained.
     * @param {ArrayLike<number>} pocketCenter  Pocket center [x,y,z] in nm.
     * @param {number} pocketRadius     Pocket radius in nm (e.g. 1.5 nm prune).
     * @param {number} cutoff           Nonbonded cutoff in nm. Stored on the
     *   instance and reused as the default in `evaluate`.
     * @param {number} [cutoffBuffer=0] Extra retention margin in nm.
     * @returns {void}
     */
    prepareReceptor(receptor, pocketCenter, pocketRadius, cutoff, cutoffBuffer = 0) {
        const {
            coords, charges, sigma, epsilon,
        } = receptor;
        const nAll = charges.length;

        this.cutoff = cutoff;

        const cx = pocketCenter[0];
        const cy = pocketCenter[1];
        const cz = pocketCenter[2];
        const pruneR = pocketRadius + cutoff + cutoffBuffer;
        const pruneR2 = pruneR * pruneR;

        // First pass: count survivors so the compact buffers are sized exactly.
        let nKeep = 0;
        for (let a = 0; a < nAll; a++) {
            const dx = coords[3 * a] - cx;
            const dy = coords[3 * a + 1] - cy;
            const dz = coords[3 * a + 2] - cz;
            if (dx * dx + dy * dy + dz * dz <= pruneR2) {
                nKeep++;
            }
        }

        // Allocate exact-size buffers (done once per receptor, not per eval).
        const keepCoords = new Float64Array(3 * nKeep);
        const keepCharges = new Float64Array(nKeep);
        const keepSigma = new Float64Array(nKeep);
        const keepEpsilon = new Float64Array(nKeep);

        // Second pass: copy survivors into the compact buffers.
        let k = 0;
        for (let a = 0; a < nAll; a++) {
            const dx = coords[3 * a] - cx;
            const dy = coords[3 * a + 1] - cy;
            const dz = coords[3 * a + 2] - cz;
            if (dx * dx + dy * dy + dz * dz <= pruneR2) {
                keepCoords[3 * k] = coords[3 * a];
                keepCoords[3 * k + 1] = coords[3 * a + 1];
                keepCoords[3 * k + 2] = coords[3 * a + 2];
                keepCharges[k] = charges[a];
                keepSigma[k] = sigma[a];
                keepEpsilon[k] = epsilon[a];
                k++;
            }
        }

        // Precompute per-atom sqrt(epsilon) so the per-pair LJ combining in
        // evaluate() is a multiply, not a sqrt (the receptor set is static
        // across the minimization that follows).
        const keepSqrtEpsilon = new Float64Array(nKeep);
        for (let a = 0; a < nKeep; a++) keepSqrtEpsilon[a] = Math.sqrt(keepEpsilon[a]);

        this.nCandidates = nKeep;
        this.coords = keepCoords;
        this.charges = keepCharges;
        this.sigma = keepSigma;
        this.epsilon = keepEpsilon;
        this.sqrtEpsilon = keepSqrtEpsilon;
    }

    /**
     * Accumulate the ligand-receptor LJ + Coulomb energy and the *ligand-side*
     * gradient.
     *
     * For each ligand atom i and each retained receptor atom j within `cutoff`,
     * the pair energy is added and the ligand-side gradient contribution
     * `gOverR * (x_i - x_recep)` is accumulated into `grad` at atom i. The
     * receptor gradient is not formed (the receptor is static).
     *
     * Plain truncated cutoff: pairs with `r > cutoff` contribute nothing; no
     * potential-shift correction is applied. Self-interaction is impossible
     * (different molecules), and `r2 == 0` cannot arise for
     * non-overlapping atoms; an exactly-coincident pair is skipped to avoid a
     * singularity.
     *
     * @param {Float64Array} ligandCoords    Length 3*N_lig, nm.
     * @param {Float64Array} ligandCharges   Length N_lig, e.
     * @param {Float64Array} ligandSigma     Length N_lig, nm.
     * @param {Float64Array} ligandEpsilon   Length N_lig, kJ/mol.
     * @param {number}       cutoff          Nonbonded cutoff in nm.
     * @param {Float64Array} grad            Length 3*N_lig; accumulated into
     *   (ligand-side gradient). Not zeroed here -- the caller owns zeroing.
     * @returns {number}                     Total ligand-receptor energy (kJ/mol).
     */
    evaluate(ligandCoords, ligandCharges, ligandSigma, ligandEpsilon, cutoff, grad) {
        const nLig = ligandCharges.length;
        const nRec = this.nCandidates;
        const rCoords = this.coords;
        const rCharges = this.charges;
        const rSigma = this.sigma;
        const cutoff2 = cutoff * cutoff;

        // Module-shared scratch would defeat re-entrancy across instances, so
        // this length-2 buffer is cached on the instance: allocated once on the
        // first evaluate() and reused on every later call (and every pair).
        const out2 = this._out2 || (this._out2 = new Float64Array(2));

        // Receptor sqrt(epsilon): prepareReceptor builds it, but the worker's
        // rebuildReceptor assigns `epsilon` directly (fresh instance), so heal a
        // length mismatch here. O(N_rec) and only on the first evaluate after a
        // direct rebuild; the normal prepared path takes the fast no-op branch.
        if (this.sqrtEpsilon.length !== nRec) {
            const se = new Float64Array(nRec);
            for (let j = 0; j < nRec; j++) se[j] = Math.sqrt(this.epsilon[j]);
            this.sqrtEpsilon = se;
        }
        const rSqrtEps = this.sqrtEpsilon;

        // Ligand sqrt(epsilon), cached on the ligand epsilon-array identity (it
        // is constant across a minimization), so per-pair LJ combining is a
        // multiply (sqrtEps_i * sqrtEps_j) rather than a sqrt(eps_i*eps_j).
        if (this._ligEpsRef !== ligandEpsilon
            || !this._ligSqrtEps || this._ligSqrtEps.length !== nLig) {
            const ls = new Float64Array(nLig);
            for (let i = 0; i < nLig; i++) ls[i] = Math.sqrt(ligandEpsilon[i]);
            this._ligSqrtEps = ls;
            this._ligEpsRef = ligandEpsilon;
        }
        const ligSqrtEps = this._ligSqrtEps;

        let energy = 0;

        for (let i = 0; i < nLig; i++) {
            const lx = ligandCoords[3 * i];
            const ly = ligandCoords[3 * i + 1];
            const lz = ligandCoords[3 * i + 2];
            const qi = ligandCharges[i];
            const si = ligandSigma[i];
            const sei = ligSqrtEps[i];

            let gx = 0;
            let gy = 0;
            let gz = 0;

            for (let j = 0; j < nRec; j++) {
                const dx = lx - rCoords[3 * j];
                const dy = ly - rCoords[3 * j + 1];
                const dz = lz - rCoords[3 * j + 2];
                const r2 = dx * dx + dy * dy + dz * dz;

                // Plain truncated cutoff. Skip an exactly-coincident pair to
                // avoid a 1/0 singularity (should not occur physically).
                if (r2 > cutoff2 || r2 === 0) continue;

                const sigmaIJ = combineSigma(si, rSigma[j]);
                const epsIJ = sei * rSqrtEps[j];
                ljPair(r2, sigmaIJ, epsIJ, out2);
                energy += out2[0];
                let gOverR = out2[1];

                coulombPair(r2, qi * rCharges[j], out2);
                energy += out2[0];
                gOverR += out2[1];

                // Ligand-side gradient: dE/dx_i = gOverR * (x_i - x_recep).
                gx += gOverR * dx;
                gy += gOverR * dy;
                gz += gOverR * dz;
            }

            grad[3 * i] += gx;
            grad[3 * i + 1] += gy;
            grad[3 * i + 2] += gz;
        }

        return energy;
    }
}
